import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, Percent, RefreshCw } from 'lucide-react';
import type { ProviderFeeOperation } from '../../../types';
import { usePermission, useRole } from '../../../hooks/useAuth';
import { useProviderFeeConfigs, useUpsertProviderFeeConfigs } from '../../../hooks/useApi';
import { apiClient } from '../../../services/api';
import { ProviderFeeConfigItem } from '../../../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/Card';
import { LoadingOverlay, LoadingSpinner } from '../../ui/Loading';
import {
  Button,
  DataTable,
} from '@/components/ui/shadcn';
import AccessDeniedPanel from '../../ui/AccessDeniedPanel';
import ProviderFeeAddModal from './ProviderFeeAddModal';
import ProviderFeeUpdateModal from './ProviderFeeUpdateModal';
import type { EditableRow, SupportedMethod } from './providerFeesTypes';
import {
  mergeWithHardcodedSupportedMethods,
  providerSortRank,
  sortProviderFeeRows,
  toPercentString,
  toRateNumber,
  toMoneyString,
  toSupportedKey,
  networkFeeFieldsFromItem,
  networkFeePayloadFromRow,
  getProviderGroupKey,
  formatProviderDisplayName,
} from './providerFeesUtils';
import { getProviderFeeColumns, providerFeeRowId } from './providerFeesColumns';

const FEE_RESPONSIBILITY_ITEMS = [
  {
    index: '01',
    title: 'Player / Shop',
    from: 'Player',
    fromDescription: 'End user',
    to: 'Shop',
    toDescription: 'Player-facing agent',
    payer: 'Shop pays all fees',
    accentClassName: 'border-amber-200 bg-amber-50 text-amber-800',
  },
  {
    index: '02',
    title: 'Player / Platform',
    from: 'Player',
    fromDescription: 'End user',
    to: 'Platform',
    toDescription: 'Our system',
    payer: 'Platform pays all fees',
    accentClassName: 'border-violet-200 bg-violet-50 text-violet-800',
  },
  {
    index: '03',
    title: 'Platform / Agent Network',
    from: 'Platform',
    fromDescription: 'Our system',
    to: 'Agent Network',
    toDescription: 'Super Agent, Agent, Shop',
    payer: 'Agent side pays all fees. Platform pays none.',
    accentClassName: 'border-orange-200 bg-orange-50 text-orange-800',
  },
] as const;

function FeeResponsibilityGuide() {
  return (
    <section className="border-b border-border bg-muted/20 px-6 py-5">
      <div className="mb-3">
        <p className="text-sm font-semibold text-foreground">Transaction & Fee Responsibility</p>
        <p className="text-xs text-muted-foreground">
          Online transactions between entities and who pays the configured fees.
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        {FEE_RESPONSIBILITY_ITEMS.map((item) => (
          <div key={item.index} className="rounded-lg border border-border bg-background p-3">
            <div className="mb-3 flex items-center gap-2">
              <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                {item.index}
              </span>
              <span className="text-sm font-semibold text-foreground">{item.title}</span>
            </div>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <div className="rounded-md border border-blue-100 bg-blue-50 p-2">
                <p className="text-sm font-semibold text-blue-900">{item.from}</p>
                <p className="text-xs text-blue-700">{item.fromDescription}</p>
              </div>
              <span className="text-lg font-semibold text-muted-foreground" aria-label="Transaction relationship">
                ↔
              </span>
              <div className="rounded-md border border-amber-100 bg-amber-50 p-2">
                <p className="text-sm font-semibold text-amber-900">{item.to}</p>
                <p className="text-xs text-amber-700">{item.toDescription}</p>
              </div>
            </div>
            <div className={`mt-3 rounded-md border px-2 py-1.5 text-xs font-semibold ${item.accentClassName}`}>
              {item.payer}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Agent network payout fees use the selected payout provider and withdrawal method from this configuration.
      </p>
    </section>
  );
}

const ProviderFeesManagement: React.FC = () => {
  const canManage = usePermission('transactionLimitsManagement');
  // KIOSK-720: platform_admin is read-only/monitoring — can view fees but not edit them.
  const canEdit = useRole(['system_admin', 'super_admin', 'finance_admin']);
  const { data, isLoading, error, refetch, isFetching } = useProviderFeeConfigs();
  const upsertMutation = useUpsertProviderFeeConfigs();
  const isSaving = upsertMutation.isLoading;
  const isRefreshing = isFetching && !isLoading;
  const isPageBusy = isSaving || isRefreshing;
  const busyMessage = isSaving ? 'Updating...' : 'Loading...';
  const [supportedByKey, setSupportedByKey] = useState<Record<string, SupportedMethod[]>>({});

  const serverItems: ProviderFeeConfigItem[] = useMemo(() => data?.data?.items || [], [data]);
  const providerOptions: string[] = useMemo(() => data?.data?.providers || [], [data]);

  const [rows, setRows] = useState<EditableRow[]>([]);
  const [activeOperationTab, setActiveOperationTab] = useState<'deposit' | 'withdrawal'>('deposit');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draftRow, setDraftRow] = useState<EditableRow>({
    provider: '',
    operation: 'deposit',
    methodId: 'all',
    isEnabled: true,
    paymentGateFeePercent: '0',
    paymentGateFeeMinUsd: '0',
    systemFeePercent: '0',
    networkFeeType: 'none',
    networkFeePercent: '0',
    networkFeeFixedUsd: '0',
  });

  useEffect(() => {
    let cancelled = false;

    const catalogKeys = [
      'btcpay::deposit',
      'btcpay::withdrawal',
      'linkmepay::deposit',
      'linkmepay::withdrawal',
      'ggusonepay::deposit',
      'ggusonepay::withdrawal',
      'meld::deposit',
      'zeroxprocessing::deposit',
      'zeroxprocessing::withdrawal',
    ];
    const keys = Array.from(
      new Set([
        ...catalogKeys,
        ...serverItems
          .map((it) => ({
            provider: (it.provider || '').trim().toLowerCase(),
            operation: (it.operation || '').trim().toLowerCase(),
          }))
          .filter((x) => x.provider && (x.operation === 'deposit' || x.operation === 'withdrawal'))
          .map((x) => toSupportedKey(x.provider, x.operation)),
      ])
    );

    const missing = keys.filter((k) => supportedByKey[k] == null);
    if (missing.length === 0)
      return () => {
        cancelled = true;
      };

    (async () => {
      for (const key of missing) {
        const [provider, operation] = key.split('::');
        try {
          const resp = await (apiClient as any).getSupportedMethods(provider, operation, {
            includeDisabled: true,
          });
          const methods = resp?.data?.methods ?? resp?.data?.data?.methods ?? resp?.methods ?? [];
          if (!cancelled) {
            const merged = mergeWithHardcodedSupportedMethods(
              provider,
              Array.isArray(methods) ? methods : [],
              operation
            );
            setSupportedByKey((prev) => ({ ...prev, [key]: merged }));
          }
        } catch {
          if (!cancelled) {
            const merged = mergeWithHardcodedSupportedMethods(provider, [], operation);
            setSupportedByKey((prev) => ({ ...prev, [key]: merged }));
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverItems]);

  useEffect(() => {
    const makeCatalogStub = (
      provider: string,
      operation: ProviderFeeOperation,
      methodId: string,
      template?: ProviderFeeConfigItem
    ): ProviderFeeConfigItem => ({
      id: template?.id ?? '',
      provider: template?.provider ?? provider,
      operation: template?.operation ?? operation,
      methodId,
      paymentGateFeeRate: template?.paymentGateFeeRate ?? 0,
      systemFeeRate: template?.systemFeeRate ?? 0,
      feeRate: template?.feeRate ?? 0,
      isEnabled: template?.isEnabled ?? true,
    });

    const exactByKey = new Map<string, ProviderFeeConfigItem>();
    const fallbackByProviderOp = new Map<string, ProviderFeeConfigItem>();

    for (const it of serverItems) {
      const provider = (it.provider || '').toLowerCase();
      const operation = (it.operation || '').toLowerCase();
      const methodId = (it.methodId || 'all').toLowerCase();
      const key = `${provider}::${operation}::${methodId}`;
      exactByKey.set(key, it);
      if (methodId === 'all') {
        fallbackByProviderOp.set(`${provider}::${operation}`, it);
      }
    }

    const mapped: EditableRow[] = [];
    const seen = new Set<string>();

    const pushRow = (
      it: ProviderFeeConfigItem,
      methodIdOverride?: string,
      opts?: { requireInCatalog?: boolean }
    ) => {
      const provider = (it.provider || '').toLowerCase();
      const operation = (it.operation || '').toLowerCase();
      const methodId = (methodIdOverride || it.methodId || 'all').toLowerCase();
      if (provider === 'online_debt') return;
      if ((operation === 'deposit' || operation === 'withdrawal') && methodId === 'all') return;
      if (
        provider === 'btcpay' &&
        operation === 'withdrawal' &&
        !['bitcoin_transfer', 'litecoin_transfer', 'dogecoin_transfer'].includes(methodId)
      ) {
        return;
      }
      if (opts?.requireInCatalog && (operation === 'deposit' || operation === 'withdrawal')) {
        const supported = supportedByKey[toSupportedKey(provider, operation)] || [];
        const supportedIds = new Set(
          supported
            .map((m) => String(m.methodId || m.id || '').trim().toLowerCase())
            .filter(Boolean),
        );
        if (supportedIds.size > 0 && methodId !== 'all' && !supportedIds.has(methodId)) {
          return;
        }
      }
      const rowKey = `${provider}::${operation}::${methodId}`;
      if (seen.has(rowKey)) return;
      seen.add(rowKey);

      const hasSplit = it.paymentGateFeeRate != null || it.systemFeeRate != null;
      const gateRate = hasSplit ? (it.paymentGateFeeRate ?? 0) : (it.feeRate ?? 0);
      const sysRate = hasSplit ? (it.systemFeeRate ?? 0) : 0;
      mapped.push({
        provider,
        operation: it.operation,
        methodId,
        isEnabled: it.isEnabled !== false,
        paymentGateFeePercent: toPercentString(gateRate),
        paymentGateFeeMinUsd: toMoneyString(Number(it.paymentGateFeeMinUsd ?? 0)),
        systemFeePercent: toPercentString(sysRate),
        ...networkFeeFieldsFromItem(it),
        networkFeeSource: it.networkFeeSource,
      });
    };

    // 1) Keep all exact method-level rows (except all for deposit/withdrawal)
    for (const it of serverItems) {
      pushRow(it);
    }

    // 2) Expand fallback `all` into concrete methods from supported catalog
    for (const [providerOp, fallback] of Array.from(fallbackByProviderOp.entries())) {
      const [provider, operation] = providerOp.split('::');
      if (operation !== 'deposit' && operation !== 'withdrawal') continue;
      const methods = supportedByKey[toSupportedKey(provider, operation)] || [];
      for (const m of methods) {
        const mid = String(m.methodId || m.id || '').trim().toLowerCase();
        if (!mid) continue;
        const exact = exactByKey.get(`${provider}::${operation}::${mid}`);
        pushRow(exact || fallback, mid, { requireInCatalog: true });
      }
    }

    // 3) Show every catalog method for providers we manage (even if not yet in DB)
    const catalogProviders = ['btcpay', 'linkmepay', 'ggusonepay', 'meld', 'zeroxprocessing'];
    for (const provider of catalogProviders) {
      for (const operation of ['deposit', 'withdrawal'] as const) {
        const methods = supportedByKey[toSupportedKey(provider, operation)] || [];
        if (methods.length === 0) continue;
        const fallback = fallbackByProviderOp.get(`${provider}::${operation}`);
        for (const m of methods) {
          const mid = String(m.methodId || m.id || '').trim().toLowerCase();
          if (!mid) continue;
          const exact = exactByKey.get(`${provider}::${operation}::${mid}`);
          if (exact) continue;
          pushRow(makeCatalogStub(provider, operation, mid, fallback), mid, { requireInCatalog: true });
        }
      }
    }

    setRows(sortProviderFeeRows(mapped, supportedByKey));
  }, [serverItems, supportedByKey]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if ((r.provider || '').toLowerCase() === 'online_debt') return false;
      return r.operation === activeOperationTab;
    });
  }, [rows, activeOperationTab]);

  const groupedRowsByProvider = useMemo(() => {
    const groups = new Map<string, EditableRow[]>();
    for (const row of filteredRows) {
      const key = getProviderGroupKey(row);
      const prev = groups.get(key) || [];
      prev.push(row);
      groups.set(key, prev);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => {
      const rank = providerSortRank(a) - providerSortRank(b);
      if (rank !== 0) return rank;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
  }, [filteredRows]);

  const ensureSupportedMethods = useCallback(
    async (providerRaw: string, operationRaw: ProviderFeeOperation) => {
      const provider = (providerRaw || '').trim().toLowerCase();
      const operation = String(operationRaw || '').trim().toLowerCase();
      if (!provider || (operation !== 'deposit' && operation !== 'withdrawal')) return;
      const key = `${provider}::${operation}`;
      if (supportedByKey[key] != null) return;
      try {
        const resp = await (apiClient as any).getSupportedMethods(provider, operation, {
          includeDisabled: true,
        });
        const methods = resp?.data?.methods ?? resp?.data?.data?.methods ?? resp?.methods ?? [];
        const merged = mergeWithHardcodedSupportedMethods(
          provider,
          Array.isArray(methods) ? methods : [],
          operation
        );
        setSupportedByKey((prev) => ({ ...prev, [key]: merged }));
      } catch {
        const merged = mergeWithHardcodedSupportedMethods(provider, [], operation);
        setSupportedByKey((prev) => ({ ...prev, [key]: merged }));
      }
    },
    [supportedByKey],
  );

  const buildUpsertPayload = (targetRows: EditableRow[]) => ({
    partial: true,
    items: targetRows
      .map((r) => {
        const provider = (r.provider || '').trim().toLowerCase();
        const methodId = (r.methodId || 'all').trim().toLowerCase() || 'all';
        // Zerox coin methods: network fee is live from the 0xprocessing Coins API —
        // never persist the displayed live value as manual config.
        const isZeroxLiveNetworkFee = provider === 'zeroxprocessing' && methodId.startsWith('zerox_');
        return {
          provider,
          operation: r.operation,
          methodId,
          isEnabled: r.isEnabled !== false,
          paymentGateFeeRate: toRateNumber(r.paymentGateFeePercent),
          paymentGateFeeMinUsd: 0,
          systemFeeRate: toRateNumber(r.systemFeePercent),
          ...(isZeroxLiveNetworkFee ? {} : networkFeePayloadFromRow(r)),
        };
      })
      .filter((r) => r.provider.length > 0),
  });

  const openEditModal = useCallback(
    async (row: EditableRow) => {
      const idx = rows.findIndex((r) => providerFeeRowId(r) === providerFeeRowId(row));
      if (idx < 0) return;
      const r = rows[idx];
      setEditingIndex(idx);
      setDraftRow({ ...r });
      setIsUpdateModalOpen(true);
      await ensureSupportedMethods(r.provider, r.operation);
    },
    [rows, ensureSupportedMethods],
  );

  const submitDraft = async () => {
    const cleaned: EditableRow = {
      provider: (draftRow.provider || '').trim().toLowerCase(),
      operation: draftRow.operation,
      methodId:
        draftRow.operation === 'transfer'
          ? 'all'
          : (draftRow.methodId || 'all').trim().toLowerCase() || 'all',
      isEnabled: draftRow.isEnabled !== false,
      paymentGateFeePercent: String(draftRow.paymentGateFeePercent || '').trim() || '0',
      paymentGateFeeMinUsd: '0',
      systemFeePercent: String(draftRow.systemFeePercent || '').trim() || '0',
      networkFeeType: draftRow.networkFeeType,
      networkFeePercent: String(draftRow.networkFeePercent || '').trim() || '0',
      networkFeeFixedUsd: String(draftRow.networkFeeFixedUsd || '').trim() || '0',
    };
    if (!cleaned.provider) return;

    const updatedRow =
      editingIndex == null
        ? cleaned
        : { ...cleaned };

    const nextRows =
      editingIndex == null
        ? [...rows, updatedRow]
        : rows.map((r, i) => (i === editingIndex ? updatedRow : r));

    try {
      await upsertMutation.mutateAsync(buildUpsertPayload([updatedRow]));
      setRows(nextRows);
      setIsAddModalOpen(false);
      setIsUpdateModalOpen(false);
    } catch {
      // toast handled by mutation; keep modal open on failure
    }
  };

  const toggleActivity = useCallback(
    async (row: EditableRow) => {
      if (isPageBusy) return;
      if (
        (row.provider || '').toLowerCase() === 'zeroxprocessing' &&
        (row.methodId || '').toLowerCase() === 'zeroxprocessing_static_wallet'
      ) {
        return;
      }
      const updatedRow = { ...row, isEnabled: !(row.isEnabled !== false) };
      const nextRows = rows.map((r) =>
        providerFeeRowId(r) === providerFeeRowId(row) ? updatedRow : r,
      );
      try {
        await upsertMutation.mutateAsync(buildUpsertPayload([updatedRow]));
        setRows(nextRows);
      } catch {
        // toast handled by mutation
      }
    },
    [rows, upsertMutation, isPageBusy],
  );

  const columns = useMemo(
    () =>
      getProviderFeeColumns({
        supportedByKey,
        onEdit: (row) => void openEditModal(row),
        onToggle: (row) => void toggleActivity(row),
        disabled: isPageBusy || !canEdit,
      }),
    [supportedByKey, openEditModal, toggleActivity, isPageBusy, canEdit],
  );

  const emptyTableMessage = useMemo(() => {
    if (rows.length === 0) return 'No rows yet.';
    if (filteredRows.length === 0) return 'No rows match the current filters.';
    return 'No results.';
  }, [rows.length, filteredRows.length]);

  const groupTone =
    activeOperationTab === 'withdrawal'
      ? {
          border: 'border-amber-200',
          header: 'border-amber-200 bg-amber-50 text-amber-900',
          surface: 'bg-amber-50/20',
        }
      : {
          border: 'border-blue-200',
          header: 'border-blue-200 bg-blue-50 text-blue-900',
          surface: 'bg-blue-50/20',
        };

  if (!canManage) {
    return <AccessDeniedPanel message="You don't have permission to manage provider fees." />;
  }

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold text-destructive">Failed to load provider fees</p>
          <p className="mt-2 text-muted-foreground">
            {error instanceof Error ? error.message : 'An error occurred while loading the configuration.'}
          </p>
          <Button type="button" variant="outline" className="mt-4" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <ProviderFeeAddModal
        isOpen={isAddModalOpen}
        onClose={() => !isPageBusy && setIsAddModalOpen(false)}
        draftRow={draftRow}
        setDraftRow={setDraftRow}
        providerOptions={providerOptions}
        supportedByKey={supportedByKey}
        ensureSupportedMethods={ensureSupportedMethods}
        onSubmit={submitDraft}
        isSubmitting={isSaving}
      />

      <ProviderFeeUpdateModal
        isOpen={isUpdateModalOpen}
        onClose={() => !isPageBusy && setIsUpdateModalOpen(false)}
        draftRow={draftRow}
        setDraftRow={setDraftRow}
        providerOptions={providerOptions}
        supportedByKey={supportedByKey}
        ensureSupportedMethods={ensureSupportedMethods}
        onSubmit={submitDraft}
        isSubmitting={isSaving}
      />

      <LoadingOverlay isLoading={isPageBusy} message={busyMessage}>
      <Card>
        <CardHeader className="flex flex-col gap-4 space-y-0 border-b border-border pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Percent className="h-5 w-5 shrink-0" />
              Fee Configuration
            </CardTitle>
            <CardDescription>
              Configure fee settings for player deposits and player withdrawals. Fees are recorded in the wallet ledger for tracking and reconciliation.
            </CardDescription>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isPageBusy}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <FeeResponsibilityGuide />

          <div className="flex gap-2 border-b border-border px-6 py-4">
            <Button
              type="button"
              size="sm"
              variant={activeOperationTab === 'deposit' ? 'default' : 'outline'}
              onClick={() => setActiveOperationTab('deposit')}
              disabled={isPageBusy}
              className="min-w-[130px] justify-center gap-2"
            >
              <ArrowDownToLine className="h-4 w-4" />
              Deposit
            </Button>
            <Button
              type="button"
              size="sm"
              variant={activeOperationTab === 'withdrawal' ? 'default' : 'outline'}
              onClick={() => setActiveOperationTab('withdrawal')}
              disabled={isPageBusy}
              className={[
                'min-w-[130px] justify-center gap-2',
                activeOperationTab === 'withdrawal'
                  ? 'bg-amber-600 text-white hover:bg-amber-700'
                  : '',
              ].join(' ')}
            >
              <ArrowUpFromLine className="h-4 w-4" />
              Withdrawal
            </Button>
          </div>

          <div className="rounded-b-lg space-y-5 px-6 py-5">
            {groupedRowsByProvider.length === 0 ? (
              <div className="text-sm text-muted-foreground">{emptyTableMessage}</div>
            ) : (
              groupedRowsByProvider.map(([provider, providerRows]) => (
                <div key={provider} className={`rounded-lg border ${groupTone.border} ${groupTone.surface}`}>
                  <div className={`border-b px-4 py-2 text-sm font-semibold uppercase tracking-wide ${groupTone.header}`}>
                    {formatProviderDisplayName(provider)}
                  </div>
                  <DataTable<EditableRow, unknown>
                    columns={columns}
                    data={providerRows}
                    getRowId={(row) => providerFeeRowId(row)}
                    emptyMessage={emptyTableMessage}
                    className={[
                      '[&_table]:table-fixed',
                      '[&_th]:whitespace-nowrap',
                      '[&_th:nth-child(1)]:w-[240px] [&_td:nth-child(1)]:w-[240px]',
                      '[&_th:nth-child(2)]:w-[180px] [&_td:nth-child(2)]:w-[180px]',
                      '[&_th:nth-child(3)]:w-[130px] [&_td:nth-child(3)]:w-[130px]',
                      '[&_th:nth-child(4)]:w-[110px] [&_td:nth-child(4)]:w-[110px]',
                      '[&_th:nth-child(5)]:w-[130px] [&_td:nth-child(5)]:w-[130px]',
                      '[&_th:nth-child(6)]:w-[90px] [&_td:nth-child(6)]:w-[90px]',
                      '[&_th:nth-child(7)]:w-[80px] [&_td:nth-child(7)]:w-[80px]',
                    ].join(' ')}
                  />
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
      </LoadingOverlay>
    </>
  );
};

export default ProviderFeesManagement;
